import {
  LiteState,
  experiencesToComplete,
  isBasicCV,
  isCompleteExperience,
} from '@map/components/lite/utils';
import { Experience, ExperienceSkill } from '@map/types';
import { CERTIFICATION_EDUCATION_TYPE } from '@shared/constants';
import { keyBy, orderBy } from '@shared/util/array';
import { deepEqual } from '@shared/util/object';
import { useSelector } from 'react-redux';
import { Reducer } from 'redux';
import { createSelector } from 'reselect';
import type { SetRequired } from 'type-fest';
import { liteStatesSelector } from './profile';

type State = Record<number, Experience>;

/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
const EXPERIENCES_FETCHED = 'EXPERIENCES_FETCHED' as const;
const EXPERIENCE_CREATED = 'EXPERIENCE_CREATED' as const;
const LIFE_EXPERIENCES_CREATED = 'LIFE_EXPERIENCES_CREATED' as const;
const EXPERIENCE_UPDATED = 'EXPERIENCE_UPDATED' as const;
const EXPERIENCE_DESTROYED = 'EXPERIENCE_DESTROYED' as const;
/* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */

export const actionCreators = {
  experiencesFetched: (data: Experience[]) => ({ type: EXPERIENCES_FETCHED, data }) as const,
  experienceCreated: (data: Experience) => ({ type: EXPERIENCE_CREATED, data }) as const,
  lifeExperienceCreated: (data: { experiences: Experience[]; skills: ExperienceSkill[] }) =>
    ({ type: LIFE_EXPERIENCES_CREATED, data }) as const,
  experienceUpdated: (data: SetRequired<Partial<Experience>, 'id'>) =>
    ({ type: EXPERIENCE_UPDATED, data }) as const,
  experienceDestroyed: (data: Experience) => ({ type: EXPERIENCE_DESTROYED, data }) as const,
};

type Action = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const experiences: Reducer<State, Action> = (state = {}, action) => {
  switch (action.type) {
    case EXPERIENCES_FETCHED: {
      const newValue = keyBy(action.data, ({ id }) => id);
      if (deepEqual(state, newValue)) {
        return state;
      }
      return newValue;
    }
    case EXPERIENCE_CREATED:
      return { ...state, [action.data.id]: action.data };
    case LIFE_EXPERIENCES_CREATED:
      return {
        ...state,
        ...action.data.experiences.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {}),
      };
    case EXPERIENCE_UPDATED: {
      const { id } = action.data;
      if (!state[id]) {
        return state;
      }
      return { ...state, [id]: { ...state[id], ...action.data } };
    }
    case EXPERIENCE_DESTROYED: {
      const { [action.data.id]: _ignored, ...newState } = state;
      return newState;
    }
    default:
      return state;
  }
};

export default experiences;

type RootState = { experiences: State };

// SELECTORS

export const experiencePropSelector = (_: unknown, props: { experience: Pick<Experience, 'id'> }) =>
  props.experience;

export const experiencesSelector = (state: RootState) => state.experiences;

export const allExperiencesSelector = createSelector(experiencesSelector, (experiencesHash) =>
  Object.values(experiencesHash),
);

const today = new Date();
export const byStartDate = (e: Experience) => (e.startDate ? new Date(e.startDate) : today);
export const byEndDate = (e: Experience) => (e.endDate ? new Date(e.endDate) : today);

export const chronologicalExperiencesSelector = createSelector(
  allExperiencesSelector,
  (experiences) =>
    orderBy(experiences, [byEndDate, byStartDate, ({ title }) => title], ['asc', 'asc', 'desc']),
);

export const liteStateSelector = createSelector(
  allExperiencesSelector,
  liteStatesSelector,
  (experiences, liteStates): LiteState => {
    const completedExperiencesCount = experiences.filter(
      (experience) => experience.completed,
    ).length;

    if (
      completedExperiencesCount >= experiencesToComplete &&
      isBasicCV(experiences.length) &&
      isCompleteExperience(completedExperiencesCount)
    ) {
      return 'skillBasedCV';
    }

    if (
      liteStates.includes('basicCV') &&
      isBasicCV(experiences.length) &&
      isCompleteExperience(completedExperiencesCount) &&
      (!liteStates.includes('completeExperience') ||
        liteStates.indexOf('basicCV') < liteStates.indexOf('completeExperience'))
    ) {
      return 'completeExperience';
    }

    if (isBasicCV(experiences.length)) {
      return 'basicCV';
    }
    if (isCompleteExperience(completedExperiencesCount)) {
      return 'completeExperience';
    }

    if (experiences.length > 0) {
      return 'CVPreview';
    }
    return 'zero';
  },
);

export const useLiteState = () => useSelector(liteStateSelector);

export const chronologicalCvExperiencesSelector = createSelector(
  allExperiencesSelector,
  (experiences) => orderBy(experiences, [byEndDate, byStartDate, ({ id }) => id], ['desc', 'desc']),
);

export const chronologicalCertificationsSelector = createSelector(
  chronologicalExperiencesSelector,
  (experiences) => experiences.filter((exp) => exp.education_type === CERTIFICATION_EDUCATION_TYPE),
);

export const useExperiences = () => useSelector(allExperiencesSelector);

export const hasExperiencesSelector = createSelector(
  allExperiencesSelector,
  (experiences) => experiences.length > 0,
);

export const useHasExperiences = () => useSelector(hasExperiencesSelector);
